import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { SectionCard, StatusBadge, TempBadge, Empty } from "@/components/shared";
import { inr, mt, num, fmtDateTime } from "@/lib/format";
import { ArrowLeft, GitBranch, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Timeline", "Stakeholders", "Recommendations", "Supplied", "Reward"];
const STAGES = ["Registered", "Qualified", "Quoted", "Negotiation", "Won", "Lost"];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Timeline");
  const [actNote, setActNote] = useState("");
  const [actType, setActType] = useState("call");
  const [lostOpen, setLostOpen] = useState(false);

  const load = () => api.get(`/projects/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]);
  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;
  const p = d.project;

  const changeStage = async (stage) => {
    try { await api.patch(`/projects/${id}/stage`, { stage }); toast.success(`Stage → ${stage}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addActivity = async () => {
    if (!actNote) return;
    try { await api.post("/sales/activity", { master_id: id, type: actType, note: actNote }); setActNote(""); toast.success("Activity logged"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <button onClick={() => nav("/projects")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> Back to Projects</button>

      <div className="card-surface p-6 mb-6 fade-up" data-testid="master-project-header">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap"><h1 className="text-2xl font-extrabold text-slate-900">{p.name}</h1><StatusBadge status={p.stage} /><TempBadge temp={p.temperature} /></div>
            <p className="font-mono text-sm text-[var(--copper)] font-semibold mt-0.5">{p.project_id}</p>
            <p className="text-xs text-slate-500 mt-2">{p.building_type} · {p.builder_name} · {p.location}, {p.city} · Assigned: {p.assigned_to || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={p.stage} onChange={(e) => changeStage(e.target.value)} data-testid="stage-select" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button data-testid="mark-lost-btn" onClick={() => setLostOpen(true)} className="flex items-center gap-1.5 text-sm border border-red-200 text-red-600 px-3 py-2 rounded-lg hover:bg-red-50"><XCircle size={15} /> Lost</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-5">
          {[["Tonnage", mt(p.tonnage_mt)], ["Value", inr(p.est_value_inr)], ["Lead Score", `${p.lead_score}/100`], ["Conversion", `${p.conversion_score}%`], ["Supplied", mt(p.supplied_mt)]].map(([l, v]) => (
            <div key={l}><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{l}</p><p className="text-lg font-extrabold font-mono">{v}</p></div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
            {TABS.map((t) => <button key={t} data-testid={`ptab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-[var(--copper)] text-slate-900" : "border-transparent text-slate-500"}`}>{t}</button>)}
          </div>

          {tab === "Timeline" && (
            <SectionCard testid="master-project-timeline">
              <div className="mb-4 flex gap-2">
                <select value={actType} onChange={(e) => setActType(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white">{["call","meeting","site_visit","email","note"].map((x) => <option key={x}>{x}</option>)}</select>
                <input data-testid="activity-note" value={actNote} onChange={(e) => setActNote(e.target.value)} placeholder="Log an activity…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <button data-testid="add-activity-btn" onClick={addActivity} className="text-white px-3 py-2 rounded-lg text-sm font-semibold" style={{ background: "var(--copper)" }}><Plus size={16} /></button>
              </div>
              <div className="relative pl-5 border-l-2 border-slate-100 space-y-4">
                {d.timeline.length === 0 && <Empty />}
                {d.timeline.map((t) => (
                  <div key={t.id} className="relative">
                    <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-[var(--copper)] ring-4 ring-orange-50" />
                    <p className="text-sm text-slate-700">{t.text}</p>
                    <p className="text-xs text-slate-400">{t.user} · {fmtDateTime(t.timestamp)}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {tab === "Stakeholders" && (
            <SectionCard title="Stakeholder Graph" testid="stakeholder-graph">
              <div className="flex flex-col items-center mb-6">
                <div className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: "var(--navy-800)" }}>{p.name}</div>
                <GitBranch size={20} className="text-slate-300 my-2" />
                <div className="flex flex-wrap justify-center gap-3">
                  {d.stakeholders.map((s, i) => (
                    <div key={i} className={`rounded-lg border px-3 py-2 text-center ${s.payout_eligible ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                      <p className="text-sm font-medium text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400">{s.type} · {s.partner_code}</p>
                      {s.payout_eligible && <span className="text-[10px] text-emerald-600 font-semibold">★ Payout eligible</span>}
                    </div>
                  ))}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100"><th className="py-2">Partner</th><th className="py-2">Type</th><th className="py-2">Code</th><th className="py-2">Attribution</th></tr></thead>
                <tbody>{d.stakeholders.map((s, i) => <tr key={i} className="border-b border-slate-50"><td className="py-2">{s.name}</td><td className="py-2 text-slate-600">{s.type}</td><td className="py-2 font-mono text-xs">{s.partner_code}</td><td className="py-2">{s.payout_eligible ? <span className="text-emerald-600 text-xs font-semibold">1st Recommender</span> : <span className="text-slate-400 text-xs">Analytics only</span>}</td></tr>)}</tbody>
              </table>
            </SectionCard>
          )}

          {tab === "Recommendations" && (
            <SectionCard className="!p-0">
              <TblP head={["Partner", "Code", "MT", "Confidence", "Verified", "Payout"]} empty={!d.recommendations.length}>
                {d.recommendations.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="px-5 py-3">{r.partner_name}</td><td className="px-5 py-3 font-mono text-xs">{r.partner_code}</td>
                    <td className="px-5 py-3 font-mono">{mt(r.tonnage_mt)}</td><td className="px-5 py-3 font-mono">{r.confidence}%</td>
                    <td className="px-5 py-3">{r.verified ? <StatusBadge status="verified" /> : <StatusBadge status="pending" />}</td>
                    <td className="px-5 py-3">{r.payout_eligible ? <span className="text-emerald-600 text-xs font-semibold">Eligible</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                  </tr>
                ))}
              </TblP>
            </SectionCard>
          )}

          {tab === "Supplied" && (
            <SectionCard className="!p-0">
              <TblP head={["Dispatch", "Distributor", "MT", "Value", "LR No.", "Date"]} empty={!d.dispatches.length}>
                {d.dispatches.map((x) => (
                  <tr key={x.id} className="border-b border-slate-50"><td className="px-5 py-3 font-mono text-xs">{x.dispatch_no}</td><td className="px-5 py-3">{x.distributor_name}</td><td className="px-5 py-3 font-mono">{mt(x.total_mt)}</td><td className="px-5 py-3 font-mono">{inr(x.total_value)}</td><td className="px-5 py-3 font-mono text-xs">{x.lr_number}</td><td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(x.created_at)}</td></tr>
                ))}
              </TblP>
            </SectionCard>
          )}

          {tab === "Reward" && (
            <SectionCard title="Reward Attribution">
              <p className="text-sm text-slate-600 mb-3">Points accrue to the <b>first verified recommender</b> only. Supplied tonnage on this master: <b className="font-mono">{mt(p.supplied_mt)}</b>.</p>
              {d.stakeholders.filter((s) => s.payout_eligible).map((s, i) => (
                <div key={i} className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm"><b>{s.name}</b> ({s.partner_code}) earns loyalty points on every dispatch, gated by Rewards Ops approval.</div>
              ))}
            </SectionCard>
          )}
        </div>

        <div>
          <SectionCard title="Commercial Rollup">
            <div className="space-y-3 text-sm">
              {[["Quoted MT", mt(p.quoted_mt)], ["Ordered MT", mt(p.ordered_mt)], ["Supplied MT", mt(p.supplied_mt)]].map(([l, v]) => (
                <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-mono font-semibold">{v}</span></div>
              ))}
            </div>
          </SectionCard>
          <div className="h-6" />
          <SectionCard title="Quotes">
            {d.quotes.length === 0 ? <Empty /> : d.quotes.map((q) => (
              <div key={q.id} className="flex justify-between py-2 border-b border-slate-50 last:border-0 text-sm"><span className="font-mono text-xs">{q.quote_no}</span><span className="font-mono">{inr(q.total_value)}</span></div>
            ))}
          </SectionCard>
        </div>
      </div>

      {lostOpen && <LostDialog id={id} project={p} onClose={() => setLostOpen(false)} onDone={() => { setLostOpen(false); load(); toast.success("Marked as Lost Opportunity"); }} />}
    </div>
  );
}

function LostDialog({ id, project, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [competitor, setCompetitor] = useState("");
  const submit = async () => {
    if (!reason) { toast.error("Reason mandatory"); return; }
    try { await api.post("/sales/lost", { master_id: id, reason, competitor, lost_mt: project.tonnage_mt, lost_value: project.est_value_inr }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="lost-dialog">
        <h3 className="font-bold text-slate-900 mb-4">Mark Lost Opportunity</h3>
        <label className="text-xs font-semibold text-slate-600">Competitor</label>
        <input value={competitor} onChange={(e) => setCompetitor(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3 mt-1" placeholder="e.g. Mexflow" />
        <label className="text-xs font-semibold text-slate-600">Reason *</label>
        <textarea data-testid="lost-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1" rows={3} />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button data-testid="lost-submit" onClick={submit} className="px-4 py-2 text-sm text-white font-semibold rounded-lg bg-red-600">Confirm Lost</button>
        </div>
      </div>
    </div>
  );
}

function TblP({ head, children, empty }) {
  return (<div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">{head.map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead><tbody>{children}</tbody></table>{empty && <Empty />}</div>);
}
